import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { registrationService } from '../../../services/registrationService';
import { supabase } from '../../../lib/supabase';

const EventRegistrationsModal = ({ event, onClose }) => {
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionData, setRejectionData] = useState({ id: null, reason: '' });
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellationData, setCancellationData] = useState({ id: null, reason: '' });
  const [emailToast, setEmailToast] = useState(null);
  const [eftTogglingId, setEftTogglingId] = useState(null);
  const [invoiceLoadingId, setInvoiceLoadingId] = useState(null);
  const [invoiceToast, setInvoiceToast] = useState(null);
  const [paytrLoadingId, setPaytrLoadingId] = useState(null);
  const [paytrToast, setPaytrToast] = useState(null);
  const [paymentLinkToast, setPaymentLinkToast] = useState(null);
  const [isSendingLink, setIsSendingLink] = useState(false);
  const [emailSendingId, setEmailSendingId] = useState(null);
  const [emailStatus, setEmailStatus] = useState({});
  // NEW: discount editing state
  const [discountEditId, setDiscountEditId] = useState(null);
  const [discountInput, setDiscountInput] = useState('');
  const [discountSavingId, setDiscountSavingId] = useState(null);
  const [discountToast, setDiscountToast] = useState(null);
  // DELETE state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  // CC unified action loading
  const [ccApprovalLoadingId, setCcApprovalLoadingId] = useState(null);
  // Havale approval loading
  const [havaleApprovalLoadingId, setHavaleApprovalLoadingId] = useState(null);
  // Switch to EFT loading
  const [switchToEftLoadingId, setSwitchToEftLoadingId] = useState(null);
  const realtimeChannelRef = useRef(null);

  useEffect(() => {
    if (event?.id) {
      loadRegistrations();
      loadStats();
    }
  }, [event?.id]);

  // Real-time subscription: listen for status/payment_status changes on event_registrations
  useEffect(() => {
    if (!event?.id) return;

    const channel = supabase?.channel(`event-registrations-${event?.id}`)?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'event_registrations',
          filter: `event_id=eq.${event?.id}`,
        },
        (payload) => {
          const updated = payload?.new;
          if (!updated) return;
          setRegistrations((prev) =>
            prev?.map((r) => {
              if (r?.id !== updated?.id) return r;
              return {
                ...r,
                status: updated?.status ?? r?.status,
                paymentStatus: updated?.payment_status ?? r?.paymentStatus,
                payment_status: updated?.payment_status ?? r?.payment_status,
                paidAt: updated?.paid_at ?? r?.paidAt,
                paid_at: updated?.paid_at ?? r?.paid_at,
                paymentLink: updated?.payment_link ?? r?.paymentLink,
                payment_link: updated?.payment_link ?? r?.payment_link,
              };
            })
          );
          if (updated?.payment_status === 'PAID' || updated?.payment_status === 'paid') {
            loadStats();
          }
        }
      )?.subscribe();

    realtimeChannelRef.current = channel;

    return () => {
      if (realtimeChannelRef?.current) {
        supabase?.removeChannel(realtimeChannelRef?.current);
        realtimeChannelRef.current = null;
      }
    };
  }, [event?.id]);

  const loadRegistrations = async () => {
    try {
      setIsLoading(true);
      setError(null);
      let data = await registrationService?.getEventRegistrations(event?.id);
      setRegistrations(data || []);
    } catch (err) {
      console.error('Error loading registrations:', err);
      setError(err?.message || 'Rezervasyonlar yüklenirken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await registrationService?.getRegistrationStats(event?.id);
      setStats(statsData);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  // ─── ACTION 1: Unified CC Approval — generates link, sets APPROVED (valid enum), sends ONE email ───
  const handleCreditCardApprove = async (registration) => {
    if (isFreeRegistration(registration)) return handleFreeApprove(registration);
    try {
      setCcApprovalLoadingId(registration?.id);

      // Step 1: Generate Paynkolay payment link
      const { data: linkData, error: linkError } = await supabase?.functions?.invoke('process-custom-payment', {
        body: {
          intent: 'generate_event_link',
          registrationId: registration?.id,
          amount: registration?.totalPrice != null ? Number(registration?.totalPrice) : calculateRegistrationTotal(registration),
          customerName: registration?.participantName || registration?.participant_name || 'Misafir',
          customerEmail: registration?.participantEmail || registration?.participant_email || '',
          phone: registration?.participantPhone || registration?.participant_phone || '05555555555',
        }
      });

      if (linkError) throw linkError;
      if (!linkData?.payment_link) throw new Error('Sunucu link döndürmedi.');

      const link = linkData?.payment_link;

      // Step 2: Update status to 'approved' (valid DB enum lowercase) and save payment link
      try {
        const { error: dbError } = await supabase?.from('event_registrations')?.update({
          status: 'approved',
          payment_link: link,
          payment_status: 'PENDING',
        })?.eq('id', registration?.id);

        if (dbError) throw new Error('Durum güncellenemedi: ' + dbError.message);
      } catch (dbErr) {
        console.error('DB status update error:', dbErr);
        setPaymentLinkToast({ type: 'error', message: 'Veritabanı hatası: ' + (dbErr?.message || 'Durum güncellenemedi.') });
        setTimeout(() => setPaymentLinkToast(null), 8000);
        return;
      }

      // Step 3: Send ONE email with payment link (EVENT_APPROVED_PAYMENT_LINK)
      try {
        await supabase?.functions?.invoke('send-event-payment-email', {
          body: {
            customerName: registration?.participantName || registration?.participant_name || 'Misafir',
            customerEmail: registration?.participantEmail || registration?.participant_email,
            regId: registration?.id,
            frontendUrl: window.location?.origin,
            eventName: event?.title || '',
            eventDate: event?.date || event?.eventDate || '',
            eventLocation: event?.location || '',
            amount: registration?.totalPrice != null
              ? `${Number(registration?.totalPrice)?.toFixed(2)} ₺`
              : undefined,
          }
        });
      } catch (emailErr) {
        console.error('CC approval email failed (non-fatal):', emailErr);
      }

      // Update local state
      setRegistrations(prev =>
        prev?.map(r => r?.id === registration?.id
          ? { ...r, status: 'approved', paymentLink: link, payment_link: link, paymentStatus: 'PENDING' }
          : r
        )
      );
      await loadStats();

      setPaymentLinkToast({ type: 'success', message: '✅ Onaylandı! Ödeme linki oluşturuldu ve e-posta gönderildi.' });
      setTimeout(() => setPaymentLinkToast(null), 6000);
    } catch (err) {
      console.error('CC approval error:', err);
      setPaymentLinkToast({ type: 'error', message: 'Hata: ' + (err?.message || 'Bilinmeyen bir hata oluştu.') });
      setTimeout(() => setPaymentLinkToast(null), 8000);
    } finally {
      setCcApprovalLoadingId(null);
    }
  };

  // ─── ACTION 2a: Havale Initial Approval — sets APPROVED (valid enum), sends IBAN email ───
  const handleHavaleApprove = async (registration) => {
    if (isFreeRegistration(registration)) return handleFreeApprove(registration);
    try {
      setHavaleApprovalLoadingId(registration?.id);

      // Update status to 'approved' (valid DB enum lowercase)
      try {
        const { error: dbError } = await supabase?.from('event_registrations')?.update({
          status: 'approved',
          payment_status: 'PENDING',
        })?.eq('id', registration?.id);

        if (dbError) throw new Error('Durum güncellenemedi: ' + dbError.message);
      } catch (dbErr) {
        console.error('DB status update error:', dbErr);
        setEmailToast('Veritabanı hatası: ' + (dbErr?.message || 'Durum güncellenemedi.'));
        setTimeout(() => setEmailToast(null), 8000);
        return;
      }

      // Send IBAN / bank transfer info email via send-ticket-email (approved type)
      try {
        await supabase?.functions?.invoke('send-ticket-email', {
          body: {
            participantEmail: registration?.participantEmail || registration?.participant_email,
            participantName: registration?.participantName || registration?.participant_name,
            eventTitle: event?.title,
            eventDate: event?.date || event?.eventDate,
            eventTime: event?.time || event?.eventTime,
            eventLocation: event?.location,
            reservationId: registration?.id,
            emailType: 'approved',
          },
        });
      } catch (emailErr) {
        console.error('Havale approval email failed:', emailErr);
      }

      setRegistrations(prev =>
        prev?.map(r => r?.id === registration?.id
          ? { ...r, status: 'approved', paymentStatus: 'PENDING' }
          : r
        )
      );
      await loadStats();

      setEmailToast('✅ Havale onaylandı! Katılımcıya IBAN bilgisi e-postası gönderildi.');
      setTimeout(() => setEmailToast(null), 6000);
    } catch (err) {
      console.error('Havale approve error:', err);
      setEmailToast('Hata: ' + (err?.message || 'Bilinmeyen bir hata oluştu.'));
      setTimeout(() => setEmailToast(null), 6000);
    } finally {
      setHavaleApprovalLoadingId(null);
    }
  };

  // ─── ACTION 2b: Havale Payment Received — sets PAID, sends ticket email (REUSES existing EFT toggle) ───
  const handleConfirmHavalePayment = async (registrationId) => {
    try {
      setEftTogglingId(registrationId);
      const registration = registrations?.find(r => r?.id === registrationId);

      // Step A: Update DB status to 'paid'
      const { error: dbError } = await supabase?.from('event_registrations')?.update({
        status: 'paid',
        payment_status: 'PAID',
        paid_at: new Date()?.toISOString(),
      })?.eq('id', registrationId);

      if (dbError) throw dbError;

      // Step B: Send Digital Ticket Email
      if (registration) {
        const { error: mailError } = await supabase?.functions?.invoke('send-event-payment-email', {
          body: {
            customerName: registration?.participant_name || registration?.participantName || 'Misafir',
            customerEmail: registration?.participant_email || registration?.participantEmail,
            regId: registration?.id,
            templateType: 'EVENT_PAYMENT_SUCCESS_TICKET',
            frontendUrl: window.location?.origin,
            eventName: event?.title || '',
            eventDate: event?.date || event?.eventDate || '',
            eventLocation: event?.location || '',
            amount: registration?.totalPrice != null
              ? `${Number(registration?.totalPrice)?.toFixed(2)} ₺`
              : undefined,
          }
        });
        if (mailError) console.error("Ticket email error:", mailError);
      }

      setRegistrations(prev =>
        prev?.map(r => r?.id === registrationId
          ? { ...r, status: 'paid', paymentStatus: 'PAID', payment_status: 'PAID' }
          : r
        )
      );
      await loadStats();

      setEmailToast('✅ Havale ödemesi onaylandı ve dijital bilet e-posta ile gönderildi!');
      setTimeout(() => setEmailToast(null), 6000);
    } catch (err) {
      console.error("Havale confirm error:", err);
      setEmailToast('İşlem başarısız: ' + (err?.message || 'Bilinmeyen hata'));
      setTimeout(() => setEmailToast(null), 6000);
    } finally {
      setEftTogglingId(null);
    }
  };

  // ─── SWITCH TO EFT: Changes payment_method from credit_card to bank_transfer ───
  const handleSwitchToEFT = async (registrationId) => {
    try {
      setSwitchToEftLoadingId(registrationId);

      const { error: dbError } = await supabase
        ?.from('event_registrations')
        ?.update({ payment_method: 'bank_transfer' })
        ?.eq('id', registrationId);

      if (dbError) throw dbError;

      // Update local state so UI re-renders immediately
      setRegistrations(prev =>
        prev?.map(r => r?.id === registrationId
          ? { ...r, paymentMethod: 'bank_transfer', payment_method: 'bank_transfer' }
          : r
        )
      );

      setEmailToast('✅ Ödeme yöntemi EFT/Havale olarak güncellendi. Artık "Havale Ödemesi Alındı" butonu görünür.');
      setTimeout(() => setEmailToast(null), 6000);
    } catch (err) {
      console.error('Switch to EFT error:', err);
      setEmailToast('Hata: ' + (err?.message || 'Ödeme yöntemi güncellenemedi.'));
      setTimeout(() => setEmailToast(null), 6000);
    } finally {
      setSwitchToEftLoadingId(null);
    }
  };

  const handleRejectClick = (registrationId) => {
    setRejectionData({ id: registrationId, reason: '' });
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async () => {
    try {
      setProcessingId(rejectionData?.id);

      const rejectedId = rejectionData?.id;
      const registration = registrations?.find(r => r?.id === rejectedId);

      // Update to 'cancelled' (valid DB enum lowercase)
      await supabase?.from('event_registrations')?.update({
        status: 'cancelled',
        rejection_reason: rejectionData?.reason || '',
      })?.eq('id', rejectedId);

      await loadRegistrations();
      await loadStats();
      setShowRejectModal(false);
      setRejectionData({ id: null, reason: '' });

      if (registration) {
        try {
          await supabase?.functions?.invoke('send-cancellation-email', {
            body: {
              participantEmail: registration?.participantEmail,
              participantName: registration?.participantName,
              eventTitle: event?.title,
              eventDate: event?.date || event?.eventDate,
              eventTime: event?.time || event?.eventTime,
              eventLocation: event?.location,
              reservationId: rejectedId,
              cancellationReason: rejectionData?.reason || '',
            },
          });
        } catch (emailErr) {
          console.error('Rejection email failed:', emailErr);
        }
      }
    } catch (err) {
      console.error('Error rejecting registration:', err);
      setError(err?.message || 'Reddetme sırasında bir hata oluştu');
    } finally {
      setProcessingId(null);
    }
  };

  const handleCancelClick = (registrationId) => {
    setCancellationData({ id: registrationId, reason: '' });
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async () => {
    try {
      setProcessingId(cancellationData?.id);

      const registration = registrations?.find(r => r?.id === cancellationData?.id);

      // Update to 'cancelled' (valid DB enum lowercase)
      await supabase?.from('event_registrations')?.update({
        status: 'cancelled',
        organizer_cancellation_response: cancellationData?.reason || '',
        cancellation_processed_at: new Date()?.toISOString(),
      })?.eq('id', cancellationData?.id);

      await loadRegistrations();
      await loadStats();
      setShowCancelModal(false);
      setCancellationData({ id: null, reason: '' });

      if (registration) {
        try {
          await supabase?.functions?.invoke('send-cancellation-email', {
            body: {
              participantEmail: registration?.participantEmail,
              participantName: registration?.participantName,
              eventTitle: event?.title,
              eventDate: event?.date || event?.eventDate,
              eventTime: event?.time || event?.eventTime,
              eventLocation: event?.location,
              reservationId: cancellationData?.id,
              cancellationReason: cancellationData?.reason || '',
            },
          });
        } catch (emailErr) {
          console.error('Cancellation email failed:', emailErr);
        }
      }
    } catch (err) {
      console.error('Error cancelling registration:', err);
      setError(err?.message || 'İptal sırasında bir hata oluştu');
    } finally {
      setProcessingId(null);
    }
  };

  const handleCreateInvoiceDraft = async (registration) => {
    try {
      setInvoiceLoadingId(registration?.id);

      let taxId = '';
      let billingType = 'individual';
      let taxOffice = 'Bireysel';
      let companyName = '';
      let streetAddress = 'Merkez / İZMİR';

      const resolvedUserId = registration?.userId || registration?.user_id || registration?.user?.id;

      if (resolvedUserId) {
        const { data: profile } = await supabase
          ?.from('user_profiles')
          ?.select('tc_number, address_line, city, district')
          ?.eq('id', resolvedUserId)
          ?.maybeSingle();

        if (profile?.address_line || profile?.city) {
          const parts = [profile?.address_line, profile?.district, profile?.city]?.filter(Boolean);
          if (parts?.length > 0) streetAddress = parts?.join(', ');
        }

        const realTC = profile?.tc_number;
        if (realTC && realTC?.trim()?.length >= 10) {
          taxId = realTC?.trim();
          billingType = 'individual';
        } else {
          const reservationTC = registration?.tcNumber || registration?.tc_number;
          if (reservationTC && reservationTC?.trim()?.length >= 10) taxId = reservationTC?.trim();
          billingType = 'individual';
        }
      } else {
        const reservationTC = registration?.tcNumber || registration?.tc_number;
        if (reservationTC && reservationTC?.trim()?.length >= 10) taxId = reservationTC?.trim();
      }

      const finalTaxId = taxId ? taxId?.replace(/\D/g, '') || '11111111111' : '11111111111';
      const finalDigitCount = finalTaxId?.length;
      if (finalDigitCount === 10) {
        billingType = 'corporate';
        taxOffice = taxOffice === 'Bireysel' ? 'Kurumsal' : taxOffice;
      } else {
        billingType = 'individual';
      }

      const productName = `${event?.title || 'Etkinlik'} Hizmet Bedeli`;
      const totalAmount = registration?.totalPrice != null
        ? Number(registration?.totalPrice)
        : calculateRegistrationTotal(registration);

      const { data, error: fnError } = await supabase?.functions?.invoke('send-mysoft-invoice', {
        body: {
          directInvoice: true,
          source: 'EVENT_RESERVATION',
          participantName: registration?.participantName,
          participantEmail: registration?.participantEmail,
          participantPhone: registration?.participantPhone || '',
          productName,
          totalAmount,
          taxId: finalTaxId,
          billingType,
          taxOffice,
          companyName,
          streetAddress,
          registrationId: registration?.id,
          eventTitle: event?.title,
          eventId: event?.id || null,
        },
      });

      if (fnError) {
        let errorMsg = fnError?.message || 'Edge function hatası';
        try {
          const ctx = fnError?.context;
          if (ctx) {
            const bodyText = typeof ctx?.text === 'function' ? await ctx?.text() : null;
            if (bodyText) {
              const parsed = JSON.parse(bodyText);
              if (parsed?.error) errorMsg = parsed?.error;
              else if (parsed?.detail) errorMsg = parsed?.detail;
            }
          }
        } catch (_) {}
        throw new Error(errorMsg);
      }
      if (data && !data?.success) throw new Error(data?.error || 'Fatura oluşturulamadı');

      setInvoiceToast({ type: 'success', message: `"${registration?.participantName}" için fatura taslağı başarıyla oluşturuldu.` });
      setTimeout(() => setInvoiceToast(null), 6000);
    } catch (err) {
      console.error('Invoice draft error:', err);
      setInvoiceToast({ type: 'error', message: `Fatura oluşturulamadı: ${err?.message}` });
      setTimeout(() => setInvoiceToast(null), 6000);
    } finally {
      setInvoiceLoadingId(null);
    }
  };

  // NEW: Handle saving admin-overridden price to DB
  const handleSaveDiscountedPrice = async (registration) => {
    const newPrice = parseFloat(discountInput);
    if (isNaN(newPrice) || newPrice < 0) {
      setDiscountToast({ type: 'error', message: 'Geçerli bir tutar giriniz.' });
      setTimeout(() => setDiscountToast(null), 4000);
      return;
    }
    try {
      setDiscountSavingId(registration?.id);
      const { data: updatedRows, error: updateError } = await supabase?.from('event_registrations')?.update({ total_price: newPrice })?.eq('id', registration?.id)?.select('id, total_price');
      if (updateError) throw updateError;
      if (!updatedRows || updatedRows?.length === 0) {
        throw new Error('Güncelleme başarısız: Kayıt bulunamadı veya yetki reddedildi (RLS).');
      }
      // Re-fetch from DB to confirm persistence
      await loadRegistrations();
      setDiscountEditId(null);
      setDiscountInput('');
      const label = newPrice === 0 ? 'Ücretsiz Katılım olarak işaretlendi.' : `Yeni tutar ${newPrice?.toFixed(2)} ₺ olarak kaydedildi.`;
      setDiscountToast({ type: 'success', message: label });
      setTimeout(() => setDiscountToast(null), 5000);
    } catch (err) {
      console.error('Discount save error:', err);
      setDiscountToast({ type: 'error', message: `Tutar kaydedilemedi: ${err?.message}` });
      setTimeout(() => setDiscountToast(null), 5000);
    } finally {
      setDiscountSavingId(null);
    }
  };

  const handleGeneratePaymentLink = async (reg) => {
    try {
      setPaytrLoadingId(reg?.id);
      // NO SECRETS CHECKED HERE. BLIND CALL TO EDGE FUNCTION.
      const { data, error } = await supabase?.functions?.invoke('process-custom-payment', {
        body: {
          intent: 'generate_event_link',
          registrationId: reg?.id,
          amount: reg?.totalPrice != null ? Number(reg?.totalPrice) : calculateRegistrationTotal(reg),
          customerName: reg?.participantName || reg?.participant_name || 'Misafir',
          customerEmail: reg?.participantEmail || reg?.participant_email || 'test@test.com',
          phone: reg?.participantPhone || reg?.participant_phone || '05555555555',
        }
      });

      if (error) throw error;
      if (!data || !data?.payment_link) throw new Error("Sunucu link döndürmedi.");

      const link = data?.payment_link;

      // ACTION 1: Save the generated link to the database
      const { error: dbError } = await supabase?.from('event_registrations')?.update({ payment_link: link })?.eq('id', reg?.id);

      if (dbError) throw new Error("Link oluşturuldu ancak veritabanına kaydedilemedi: " + dbError.message);

      // SUCCESS: UPDATE UI
      setRegistrations(prev =>
        prev?.map(r => r?.id === reg?.id
          ? {
              ...r,
              paytrLink: link,
              paymentLink: link,
              payment_link: link,
              paymentStatus: 'link_generated',
            }
          : r
        )
      );

      setPaytrToast({ type: 'success', message: 'Ödeme linki başarıyla oluşturuldu!' });
      setTimeout(() => setPaytrToast(null), 6000);
    } catch (err) {
      console.error('Link generation failed:', err);
      setPaytrToast({ type: 'error', message: 'Hata: ' + (err?.message || 'Bilinmeyen bir hata oluştu.') });
      setTimeout(() => setPaytrToast(null), 10000);
    } finally {
      setPaytrLoadingId(null);
    }
  };

  const handleSendEmail = async (reg, link) => {
    if (!link) {
      setPaymentLinkToast({ type: 'error', message: 'Önce ödeme linki oluşturmalısınız!' });
      setTimeout(() => setPaymentLinkToast(null), 5000);
      return;
    }

    setEmailStatus(prev => ({ ...prev, [reg?.id]: 'sending' }));

    try {
      const { error } = await supabase?.functions?.invoke('send-event-payment-email', {
        body: {
          customerName: reg?.participant_name || reg?.participantName || 'Misafir',
          customerEmail: reg?.participant_email || reg?.participantEmail,
          regId: reg?.id,
          frontendUrl: window.location?.origin,
          eventName: event?.title || event?.name || '',
          eventDate: event?.date || event?.eventDate || '',
          eventLocation: event?.location || '',
          amount: reg?.totalPrice != null
            ? `${Number(reg?.totalPrice)?.toFixed(2)} ₺`
            : undefined,
        }
      });

      if (error) throw error;

      setEmailStatus(prev => ({ ...prev, [reg?.id]: 'sent' }));
      setPaymentLinkToast({ type: 'success', message: '✅ E-posta başarıyla gönderildi!' });
      setTimeout(() => setPaymentLinkToast(null), 5000);
    } catch (err) {
      console.error('Email API error:', err);
      setEmailStatus(prev => ({ ...prev, [reg?.id]: undefined }));
      setPaymentLinkToast({ type: 'error', message: 'E-posta gönderilemedi: ' + (err?.message || 'Bilinmeyen hata') });
      setTimeout(() => setPaymentLinkToast(null), 6000);
    }
  };

  const statusOptions = [
    { value: 'all', label: 'Tüm Durumlar' },
    { value: 'Aktif Kayıtlı', label: '✅ Aktif Kayıtlı' },
    { value: 'İnceleniyor', label: '⏳ İnceleniyor' },
    { value: 'Ödeme Bekliyor', label: '💳 Ödeme Bekliyor' },
    { value: 'Ödendi', label: '💚 Ödendi' },
    { value: 'İptal / Red', label: '❌ İptal / Red' },
  ];

  // ─── ACTION 4: Color-coded status badges (pill format) ───
  const getStatusBadge = (status) => {
    const s = (status || '')?.toUpperCase();

    if (s === 'PENDING') {
      return (
        <span className="bg-amber-100 text-amber-800 text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap">⏳ Onay Bekliyor</span>
      );
    }
    if (s === 'APPROVED') {
      return (
        <span className="bg-blue-100 text-blue-800 text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap">💳 Onaylandı / Ödeme Bekliyor</span>
      );
    }
    if (s === 'PAID') {
      return (
        <span className="bg-emerald-100 text-emerald-800 text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap">✅ Ödendi</span>
      );
    }
    if (s === 'CANCELLED' || s === 'CANCELED' || s === 'cancelled') {
      return (
        <span className="bg-red-100 text-red-800 text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap">❌ İptal Edildi</span>
      );
    }
    if (s === 'REJECTED' || s === 'rejected') {
      return (
        <span className="bg-red-100 text-red-800 text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap">❌ Reddedildi</span>
      );
    }
    // fallback
    return (
      <span className="bg-amber-100 text-amber-800 text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap">⏳ Onay Bekliyor</span>
    );
  };

  const getPaymentStatusBadge = (registration) => {
    const isPaid =
      registration?.status === 'PAID' ||
      registration?.status === 'paid' ||
      registration?.paymentStatus === 'PAID' ||
      registration?.paymentStatus === 'paid' ||
      registration?.payment_status === 'PAID' ||
      registration?.payment_status === 'paid' ||
      registration?.havaleEftPaid;

    if (isPaid) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
          Ödendi
        </span>
      );
    }
    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateRegistrationTotal = (registration) => {
    const basePrice = registration?.equipmentRental
      ? (event?.priceWithRental || 0)
      : (event?.priceOwnBoard || 0);

    let total = basePrice;

    if (registration?.wantsLifeVest && event?.priceLifeVest > 0) total += event?.priceLifeVest;
    if (registration?.wantsSupMotor && event?.priceSupMotor > 0) total += event?.priceSupMotor;
    if (registration?.wantsWaterproofBag && event?.priceWaterproofBag > 0) total += event?.priceWaterproofBag;
    if (registration?.wantsCancellationInsurance) total += total * 0.10;

    return total;
  };

  // Helper: is this a credit card registration?
  const isCreditCard = (reg) => {
    const pm = (reg?.paymentMethod || reg?.payment_method || '')?.toLowerCase();
    return pm?.includes('kredi') || pm?.includes('credit') || pm?.includes('kart');
  };

  // Helper: is this a havale/EFT registration?
  const isHavale = (reg) => {
    const pm = (reg?.paymentMethod || reg?.payment_method || '')?.toLowerCase();
    return pm?.includes('havale') || pm?.includes('eft') || pm?.includes('transfer');
  };

  // Helper: is status in "pending" family
  const isPendingStatus = (status) => {
    const s = (status || '')?.toUpperCase();
    return s === 'PENDING' || s === 'pending';
  };

  // Helper: is status in "approved waiting payment" family
  const isAwaitingPayment = (status) => {
    const s = (status || '')?.toUpperCase();
    return s === 'APPROVED';
  };

  // Helper: is status PAID
  const isPaidStatus = (status) => {
    const s = (status || '')?.toUpperCase();
    return s === 'PAID';
  };

  // Helper: is status canceled
  const isCanceledStatus = (status) => {
    const s = (status || '')?.toUpperCase();
    return s === 'CANCELED' || s === 'CANCELLED' || s === 'cancelled' || s === 'REJECTED' || s === 'rejected';
  };

  // Helper: is this a free (0 TL) registration?
  const isFreeRegistration = (registration) => {
    let total = registration?.totalPrice != null
      ? Number(registration?.totalPrice)
      : calculateRegistrationTotal(registration);
    if (total === 0) return true;
    // Also check ticket_type field for 'ücretsiz' keyword
    const ticketType = (registration?.ticket_type || registration?.ticketType || '')?.toLowerCase();
    if (ticketType?.includes('ücretsiz') || ticketType?.includes('free')) return true;
    // Check total_amount field directly
    const totalAmount = Number(registration?.total_amount || 0);
    return totalAmount === 0 && (registration?.total_amount !== undefined && registration?.total_amount !== null);
  };

  // ─── ACTION 1: Free Participation Approval — sets status directly to 'paid' ───
  const handleFreeApprove = async (registration) => {
    try {
      setProcessingId(registration?.id);

      const { error: dbError } = await supabase?.from('event_registrations')?.update({
        status: 'paid',
        payment_status: 'PAID',
        paid_at: new Date()?.toISOString(),
      })?.eq('id', registration?.id);

      if (dbError) throw dbError;

      // Send digital ticket email
      try {
        await supabase?.functions?.invoke('send-event-payment-email', {
          body: {
            customerName: registration?.participantName || registration?.participant_name || 'Misafir',
            customerEmail: registration?.participantEmail || registration?.participant_email,
            regId: registration?.id,
            templateType: 'EVENT_PAYMENT_SUCCESS_TICKET',
            frontendUrl: window.location?.origin,
            eventName: event?.title || '',
            eventDate: event?.date || event?.eventDate || '',
            eventLocation: event?.location || '',
            amount: '0.00 ₺',
          }
        });
      } catch (emailErr) {
        console.error('Free approve ticket email failed (non-fatal):', emailErr);
      }

      setRegistrations(prev =>
        prev?.map(r => r?.id === registration?.id
          ? { ...r, status: 'paid', paymentStatus: 'PAID', payment_status: 'PAID' }
          : r
        )
      );
      await loadStats();

      setEmailToast('🎁 Ücretsiz katılım onaylandı ve dijital bilet gönderildi!');
      setTimeout(() => setEmailToast(null), 6000);
    } catch (err) {
      console.error('Free approve error:', err);
      setEmailToast('Hata: ' + (err?.message || 'Bilinmeyen bir hata oluştu.'));
      setTimeout(() => setEmailToast(null), 6000);
    } finally {
      setProcessingId(null);
    }
  };

  // ─── ACTION 2: Card background tint based on status ───
  const getCardBgClass = (status) => {
    const s = (status || '')?.toLowerCase();
    if (s === 'paid') return 'bg-emerald-50/60 border-emerald-200';
    if (s === 'approved') return 'bg-blue-50/60 border-blue-200';
    if (s === 'pending') return 'bg-amber-50/60 border-amber-200';
    if (s === 'cancelled' || s === 'canceled' || s === 'rejected') return 'bg-red-50/60 border-red-200';
    return 'bg-muted/30 border-border';
  };

  const filteredRegistrations = registrations?.filter(reg => {
    const s = reg?.status?.toLowerCase();
    let matchesStatus = true;
    if (statusFilter === 'Aktif Kayıtlı') matchesStatus = ['pending', 'approved', 'paid']?.includes(s);
    else if (statusFilter === 'İnceleniyor') matchesStatus = s === 'pending';
    else if (statusFilter === 'Ödeme Bekliyor') matchesStatus = s === 'approved';
    else if (statusFilter === 'Ödendi') matchesStatus = s === 'paid';
    else if (statusFilter === 'İptal / Red') matchesStatus = ['cancelled', 'canceled', 'rejected']?.includes(s);
    // else statusFilter === 'all' → matchesStatus stays true
    const matchesSearch = reg?.participantName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         reg?.participantEmail?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         reg?.participantPhone?.includes(searchTerm);
    return matchesStatus && matchesSearch;
  });

  const handleDeleteClick = (registrationId) => {
    setDeleteTargetId(registrationId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    try {
      setDeletingId(deleteTargetId);
      const { error: deleteError, count } = await supabase
        ?.from('event_registrations')
        ?.delete({ count: 'exact' })
        ?.eq('id', deleteTargetId);
      if (deleteError) throw deleteError;
      if (count === 0) {
        throw new Error('Rezervasyon silinemedi: Yetki reddedildi veya kayıt bulunamadı (RLS).');
      }
      setRegistrations(prev => prev?.filter(r => r?.id !== deleteTargetId));
      await loadStats();
      setShowDeleteModal(false);
      setDeleteTargetId(null);
    } catch (err) {
      console.error('Delete registration error:', err);
      setError(err?.message || 'Rezervasyon silinirken bir hata oluştu');
      setShowDeleteModal(false);
      setDeleteTargetId(null);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-6xl max-h-[95dvh] sm:max-h-[90vh] flex flex-col overflow-hidden relative">
        {/* Email Toast Notification */}
        {emailToast && (
          <div className="fixed top-6 right-6 z-[70] bg-warning/10 border border-warning/30 text-warning rounded-lg px-4 py-3 flex items-center gap-3 shadow-lg max-w-sm">
            <Icon name="AlertTriangle" size={18} className="shrink-0" />
            <p className="text-sm font-medium">{emailToast}</p>
            <button onClick={() => setEmailToast(null)} className="ml-auto shrink-0">
              <Icon name="X" size={16} />
            </button>
          </div>
        )}
        {/* Payment Link Sent Toast */}
        {paymentLinkToast && (
          <div className={`fixed top-6 right-6 z-[70] rounded-lg px-4 py-3 flex items-center gap-3 shadow-lg max-w-sm ${
            paymentLinkToast?.type === 'success' ? 'bg-success/10 border border-success/30 text-success' :
            paymentLinkToast?.type === 'loading'? 'bg-blue-50 border border-blue-200 text-blue-700' : 'bg-error/10 border border-error/30 text-error'
          }`}>
            <Icon name={paymentLinkToast?.type === 'success' ? 'CheckCircle' : paymentLinkToast?.type === 'loading' ? 'Loader' : 'AlertCircle'} size={18} className={`shrink-0 ${paymentLinkToast?.type === 'loading' ? 'animate-spin' : ''}`} />
            <p className="text-sm font-medium">{paymentLinkToast?.message}</p>
            <button onClick={() => setPaymentLinkToast(null)} className="ml-auto shrink-0">
              <Icon name="X" size={16} />
            </button>
          </div>
        )}
        {/* Discount Toast Notification */}
        {discountToast && (
          <div className={`fixed top-6 right-6 z-[70] rounded-lg px-4 py-3 flex items-center gap-3 shadow-lg max-w-sm ${
            discountToast?.type === 'success' ? 'bg-success/10 border border-success/30 text-success' : 'bg-error/10 border border-error/30 text-error'
          }`}>
            <Icon name={discountToast?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={18} className="shrink-0" />
            <p className="text-sm font-medium">{discountToast?.message}</p>
            <button onClick={() => setDiscountToast(null)} className="ml-auto shrink-0">
              <Icon name="X" size={16} />
            </button>
          </div>
        )}
        {/* Invoice Toast Notification */}
        {invoiceToast && (
          <div className={`fixed top-6 left-1/2 -translate-x-1/2 z-[70] rounded-lg px-4 py-3 flex items-center gap-3 shadow-lg max-w-sm w-full ${
            invoiceToast?.type === 'success' ? 'bg-success/10 border border-success/30 text-success' : 'bg-error/10 border border-error/30 text-error'
          }`}>
            <Icon name={invoiceToast?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={18} className="shrink-0" />
            <p className="text-sm font-medium">{invoiceToast?.message}</p>
            <button onClick={() => setInvoiceToast(null)} className="ml-auto shrink-0">
              <Icon name="X" size={16} />
            </button>
          </div>
        )}
        {/* PayTR Toast Notification */}
        {paytrToast && (
          <div className={`fixed top-20 left-1/2 -translate-x-1/2 z-[70] rounded-lg px-4 py-3 flex items-center gap-3 shadow-lg max-w-sm w-full ${
            paytrToast?.type === 'success' ? 'bg-success/10 border border-success/30 text-success' : 'bg-error/10 border border-error/30 text-error'
          }`}>
            <Icon name={paytrToast?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={18} className="shrink-0" />
            <p className="text-sm font-medium">{paytrToast?.message}</p>
            <button onClick={() => setPaytrToast(null)} className="ml-auto shrink-0">
              <Icon name="X" size={16} />
            </button>
          </div>
        )}
        {/* Header */}
        <div className="flex-none bg-gradient-to-r from-primary to-secondary p-4 sm:p-6 rounded-t-xl text-white border-b border-gray-100 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold mb-2">Etkinlik Rezervasyonları</h2>
            <p className="text-white/90">{event?.title}</p>
          </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-smooth"
            >
              <Icon name="X" size={20} />
            </button>
        </div>

        {/* Scrollable middle section */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">

        {/* Stats */}
        {stats && (
          <div className="border-b border-border pb-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div
                className={`rounded-lg p-4 cursor-pointer transition-all ${statusFilter === 'Aktif Kayıtlı' ? 'ring-2 ring-primary bg-muted/60' : 'bg-muted/30 hover:bg-muted/50'}`}
                onClick={() => setStatusFilter(statusFilter === 'Aktif Kayıtlı' ? 'all' : 'Aktif Kayıtlı')}
              >
                <p className="text-sm text-muted-foreground mb-1">Aktif Kayıtlı</p>
                <p className="text-2xl font-bold text-foreground">
                  {stats?.registered ?? 0}
                  {event?.max_participants ? (
                    <span className="text-base font-normal text-muted-foreground"> / {event?.max_participants}</span>
                  ) : null}
                </p>
              </div>
              <div
                className={`rounded-lg p-4 cursor-pointer transition-all ${statusFilter === 'İnceleniyor' ? 'ring-2 ring-yellow-400 bg-yellow-100' : 'bg-yellow-50 hover:bg-yellow-100'}`}
                onClick={() => setStatusFilter(statusFilter === 'İnceleniyor' ? 'all' : 'İnceleniyor')}
              >
                <p className="text-sm text-muted-foreground mb-1">⏳ İnceleniyor</p>
                <p className="text-2xl font-bold text-yellow-600">{stats?.pending}</p>
              </div>
              <div
                className={`rounded-lg p-4 cursor-pointer transition-all ${statusFilter === 'Ödeme Bekliyor' ? 'ring-2 ring-blue-400 bg-blue-100' : 'bg-blue-50 hover:bg-blue-100'}`}
                onClick={() => setStatusFilter(statusFilter === 'Ödeme Bekliyor' ? 'all' : 'Ödeme Bekliyor')}
              >
                <p className="text-sm text-muted-foreground mb-1">💳 Ödeme Bekliyor</p>
                <p className="text-2xl font-bold text-blue-600">{registrations?.filter(r => r?.status?.toLowerCase() === 'approved')?.length || 0}</p>
              </div>
              <div
                className={`rounded-lg p-4 cursor-pointer transition-all ${statusFilter === 'Ödendi' ? 'ring-2 ring-emerald-400 bg-emerald-100' : 'bg-emerald-50 hover:bg-emerald-100'}`}
                onClick={() => setStatusFilter(statusFilter === 'Ödendi' ? 'all' : 'Ödendi')}
              >
                <p className="text-sm text-muted-foreground mb-1">✅ Ödendi</p>
                <p className="text-2xl font-bold text-emerald-600">{registrations?.filter(r => r?.status?.toLowerCase() === 'paid')?.length || 0}</p>
              </div>
              <div
                className={`rounded-lg p-4 cursor-pointer transition-all ${statusFilter === 'İptal / Red' ? 'ring-2 ring-red-400 bg-red-100' : 'bg-red-50 hover:bg-red-100'}`}
                onClick={() => setStatusFilter(statusFilter === 'İptal / Red' ? 'all' : 'İptal / Red')}
              >
                <p className="text-sm text-muted-foreground mb-1">❌ İptal / Red</p>
                <p className="text-2xl font-bold text-red-600">{(stats?.rejected || 0) + (stats?.cancelled || 0)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="border-b border-border pb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="search"
              placeholder="İsim, email veya telefon ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
            />
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Durum filtrele"
            />
          </div>
        </div>

        {/* Content */}
        <div>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 text-error">
              <p className="font-medium mb-2">Hata</p>
              <p className="text-sm">{error}</p>
              <Button variant="outline" size="sm" onClick={loadRegistrations} className="mt-4">
                Tekrar Dene
              </Button>
            </div>
          ) : filteredRegistrations?.length > 0 ? (
            <div className="space-y-4">
              {filteredRegistrations?.map((registration) => (
                <div key={registration?.id} className={`border rounded-xl p-4 mb-3.5 shadow-sm transition-all ${getCardBgClass(registration?.status)}`}>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <h4 className="text-lg font-semibold text-foreground mb-1">
                            {registration?.participantName}
                          </h4>
                          <div className="space-y-1">
                            <p className="text-sm text-muted-foreground flex items-center gap-2">
                              <Icon name="Mail" size={14} />
                              {registration?.participantEmail}
                            </p>
                            {registration?.participantPhone && (
                              <p className="text-sm text-muted-foreground flex items-center gap-2">
                                <Icon name="Phone" size={14} />
                                {registration?.participantPhone}
                              </p>
                            )}
                            <p className="text-sm text-muted-foreground flex items-center gap-2">
                              <Icon name="Clock" size={14} />
                              Kayıt: {formatDate(registration?.createdAt)}
                            </p>
                          </div>
                        </div>
                        {/* Color-coded status badge */}
                        {getStatusBadge(registration?.status)}
                      </div>

                      {/* Total Price + Discount Editor */}
                      <div className="mb-2">
                        {discountEditId === registration?.id ? (
                          <div className="flex flex-wrap items-center gap-2 p-2 rounded-lg border border-orange-300 bg-orange-50">
                            <Icon name="Tag" size={14} className="text-orange-600 shrink-0" />
                            <span className="text-xs font-semibold text-orange-700">Yeni Tutar (₺):</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={discountInput}
                              onChange={(e) => setDiscountInput(e?.target?.value)}
                              className="w-28 px-2 py-1 text-xs border border-orange-300 rounded bg-white text-foreground focus:outline-none focus:ring-1 focus:ring-orange-400"
                              placeholder="0.00"
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={() => { setDiscountInput('0'); }}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-700 border border-green-300 hover:bg-green-200 transition-colors"
                            >
                              <Icon name="Gift" size={11} />
                              Ücretsiz
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSaveDiscountedPrice(registration)}
                              disabled={discountSavingId === registration?.id}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors disabled:opacity-60"
                            >
                              <Icon name={discountSavingId === registration?.id ? 'Loader' : 'Save'} size={11} />
                              {discountSavingId === registration?.id ? 'Kaydediliyor...' : 'Kaydet'}
                            </button>
                            <button
                              type="button"
                              onClick={() => { setDiscountEditId(null); setDiscountInput(''); }}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-white text-muted-foreground border border-border hover:bg-muted/30 transition-colors"
                            >
                              <Icon name="X" size={11} />
                              İptal
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 flex-wrap">
                            {registration?.totalPrice != null && Number(registration?.totalPrice) === 0 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                                <Icon name="Gift" size={12} />
                                Ücretsiz Katılım
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                                <Icon name="CreditCard" size={12} />
                                Toplam Tutar: {registration?.totalPrice != null ? Number(registration?.totalPrice)?.toFixed(2) : calculateRegistrationTotal(registration)?.toFixed(2)} ₺
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Payment Method */}
                      {registration?.paymentMethod && (
                        <div className="mb-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold border ${
                            isCreditCard(registration) ?'bg-purple-50 text-purple-700 border-purple-200' :'bg-teal-50 text-teal-700 border-teal-200'
                          }`}>
                            <Icon name={isCreditCard(registration) ? 'CreditCard' : 'Banknote'} size={12} />
                            Ödeme Yöntemi: {registration?.paymentMethod}
                          </span>
                        </div>
                      )}

                      {/* Payment Status Badge */}
                      {getPaymentStatusBadge(registration) && (
                        <div className="mb-2">
                          {getPaymentStatusBadge(registration)}
                        </div>
                      )}

                      {/* Paynkolay Payment Link Display */}
                      {(registration?.paytrLink || registration?.paytr_link || registration?.paymentLink || registration?.payment_link) && (
                        <div className="mb-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <a
                              href={registration?.paytrLink || registration?.paytr_link || registration?.paymentLink || registration?.payment_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors"
                            >
                              <Icon name="Link" size={12} />
                              Ödeme Linki Oluşturuldu
                            </a>
                            <button
                              type="button"
                              onClick={() => {
                                const safeGatewayUrl = `${window.location?.origin}/payment-gateway?regId=${registration?.id}`;
                                navigator.clipboard?.writeText(safeGatewayUrl)?.then(() => {
                                  setPaytrToast({ type: 'success', message: 'Ödeme linki kopyalandı! WhatsApp veya SMS ile iletebilirsiniz.' });
                                  setTimeout(() => setPaytrToast(null), 4000);
                                })?.catch((err) => {
                                  console.error('Copy error:', err);
                                  setPaytrToast({ type: 'error', message: 'Link kopyalanamadı.' });
                                  setTimeout(() => setPaytrToast(null), 3000);
                                });
                              }}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
                            >
                              <Icon name="Copy" size={12} />
                              Linki Kopyala
                            </button>
                            {/* Only show "resend email" for non-CC or if already in APPROVED_WAITING_PAYMENT */}
                            {!isCreditCard(registration) && (
                              <button
                                type="button"
                                disabled={emailStatus?.[registration?.id] === 'sending'}
                                onClick={() => handleSendEmail(registration, registration?.paytrLink || registration?.paytr_link || registration?.paymentLink || registration?.payment_link)}
                                className={`text-white text-xs px-3 py-1.5 rounded font-medium flex items-center gap-1 ml-2 whitespace-nowrap transition-colors ${
                                  emailStatus?.[registration?.id] === 'sending' ? 'bg-gray-400 cursor-not-allowed' :
                                  emailStatus?.[registration?.id] === 'sent'? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'
                                }`}
                              >
                                {emailStatus?.[registration?.id] === 'sending' ? '⏳ Gönderiliyor...' :
                                 emailStatus?.[registration?.id] === 'sent'? '✅ Tekrar Gönder' : '📧 E-posta ile Gönder'}
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Selected Custom Options */}
                      {registration?.selectedCustomOptions?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-muted-foreground mb-1 font-medium">Seçilen Ekstralar:</p>
                          <div className="flex flex-wrap gap-1.5">
                            {registration?.selectedCustomOptions?.map((opt, idx) => (
                              <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                                <Icon name="Star" size={11} />
                                {opt?.title} — {Number(opt?.price || 0)?.toFixed(2)} ₺
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Havale/EFT Payment Received Button — REUSED for PAID confirmation */}
                      {isHavale(registration) && !isPaidStatus(registration?.status) && !isCanceledStatus(registration?.status) && (
                        <div className="mb-3">
                          <button
                            type="button"
                            onClick={() => handleConfirmHavalePayment(registration?.id)}
                            disabled={eftTogglingId === registration?.id || registration?.havaleEftPaid}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all duration-200 select-none ${
                              registration?.havaleEftPaid || isPaidStatus(registration?.status)
                                ? 'bg-teal-50 border-teal-300 text-teal-700 shadow-sm opacity-70 cursor-not-allowed'
                                : 'bg-white border-teal-300 text-teal-700 hover:bg-teal-50 cursor-pointer'
                            } ${eftTogglingId === registration?.id ? 'opacity-60 cursor-not-allowed' : ''}`}
                          >
                            <span className={`w-4 h-4 rounded flex items-center justify-center border transition-colors duration-200 ${
                              registration?.havaleEftPaid || isPaidStatus(registration?.status)
                                ? 'bg-teal-500 border-teal-500' :'bg-white border-border'
                            }`}>
                              {(registration?.havaleEftPaid || isPaidStatus(registration?.status)) && (
                                <Icon name="Check" size={10} className="text-white" />
                              )}
                            </span>
                            <Icon name="Banknote" size={12} />
                            {eftTogglingId === registration?.id ? 'İşleniyor...' : 'Havale Ödemesi Alındı → Bilet Gönder'}
                          </button>
                        </div>
                      )}

                      {/* Ticket Type Badge */}
                      {registration?.ticketType && (
                        <div className="mb-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold border ${
                            registration?.ticketType === 'rental_board' ?'bg-blue-50 text-blue-700 border-blue-200'
                              : registration?.ticketType === 'standard_ticket' ?'bg-amber-50 text-amber-700 border-amber-200' :'bg-green-50 text-green-700 border-green-200'
                          }`}>
                            <Icon name={
                              registration?.ticketType === 'rental_board' ? 'ShoppingBag' :
                              registration?.ticketType === 'standard_ticket' ? 'Ticket' : 'Package'
                            } size={11} />
                            {registration?.ticketType === 'rental_board' ? 'Kiralık Board Dahil' :
                             registration?.ticketType === 'standard_ticket' ? 'Standart Bilet / Board Yok' :
                             'Kendi Board\'u İle'}
                          </span>
                        </div>
                      )}

                      {registration?.equipmentRental && (
                        <div className="mb-2">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                            <Icon name="Package" size={11} />
                            Paddleboard Kiralama
                          </span>
                        </div>
                      )}

                      {(registration?.wantsLifeVest || registration?.wantsSupMotor || registration?.wantsWaterproofBag) && (
                        <div className="mb-2 flex flex-wrap gap-1.5">
                          {registration?.wantsLifeVest && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                              <Icon name="Shield" size={11} />
                              Can Yeleği
                            </span>
                          )}
                          {registration?.wantsSupMotor && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                              <Icon name="Zap" size={11} />
                              SUP Motoru
                            </span>
                          )}
                          {registration?.wantsWaterproofBag && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                              <Icon name="Briefcase" size={11} />
                              Su Geçirmez Çanta
                            </span>
                          )}
                        </div>
                      )}

                      {registration?.wantsCancellationInsurance && (
                        <div className="mb-2">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                            <Icon name="ShieldCheck" size={11} />
                            İptal Garantisi Alındı
                          </span>
                        </div>
                      )}

                      {registration?.specialRequirements && (
                        <div className="mb-3">
                          <p className="text-xs text-muted-foreground mb-1">Özel Gereksinimler:</p>
                          <p className="text-sm text-foreground bg-background rounded-md p-2">
                            {registration?.specialRequirements}
                          </p>
                        </div>
                      )}

                      {isAwaitingPayment(registration?.status) && registration?.approvedAt && (
                        <p className="text-xs text-blue-600">
                          Onaylandı: {formatDate(registration?.approvedAt)}
                          {registration?.approver && ` - ${registration?.approver?.fullName}`}
                        </p>
                      )}

                      {isCanceledStatus(registration?.status) && registration?.rejectionReason && (
                        <div className="mt-2">
                          <p className="text-xs text-error mb-1">Red/İptal Nedeni:</p>
                          <p className="text-sm text-foreground bg-error/10 rounded-md p-2">
                            {registration?.rejectionReason}
                          </p>
                        </div>
                      )}

                      {isCanceledStatus(registration?.status) && registration?.organizerCancellationResponse && (
                        <div className="mt-2">
                          <p className="text-xs text-muted-foreground mb-1">İptal Nedeni:</p>
                          <p className="text-sm text-foreground bg-muted/50 rounded-md p-2">
                            {registration?.organizerCancellationResponse}
                          </p>
                          {registration?.cancellationProcessedAt && (
                            <p className="text-xs text-muted-foreground mt-1">
                              İptal Tarihi: {formatDate(registration?.cancellationProcessedAt)}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* ─── ACTION BUTTONS: PENDING status ─── */}
                    {isPendingStatus(registration?.status) && (
                      <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100 w-full md:w-auto md:flex-col md:min-w-[220px]">
                        {isFreeRegistration(registration) ? (
                          <button
                            type="button"
                            onClick={() => handleFreeApprove(registration)}
                            disabled={processingId === registration?.id}
                            className="bg-green-600 hover:bg-green-700 text-white font-semibold text-xs px-3.5 py-2 rounded-lg shadow-sm transition-all flex items-center gap-1.5 disabled:opacity-60 whitespace-nowrap"
                          >
                            <Icon name={processingId === registration?.id ? 'Loader' : 'Gift'} size={13} className={processingId === registration?.id ? 'animate-spin' : ''} />
                            {processingId === registration?.id ? 'İşleniyor...' : '🎁 Ücretsiz Onayla & Ödendi İşaretle'}
                          </button>
                        ) : isCreditCard(registration) ? (
                          <>
                            <button
                              type="button"
                              onClick={() => handleCreditCardApprove(registration)}
                              disabled={ccApprovalLoadingId === registration?.id}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs px-3.5 py-2 rounded-lg shadow-sm transition-all flex items-center gap-1.5 disabled:opacity-60 whitespace-nowrap"
                            >
                              <Icon name={ccApprovalLoadingId === registration?.id ? 'Loader' : 'CreditCard'} size={13} className={ccApprovalLoadingId === registration?.id ? 'animate-spin' : ''} />
                              {ccApprovalLoadingId === registration?.id ? 'İşleniyor...' : '💳 Onayla & Link Gönder'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSwitchToEFT(registration?.id)}
                              disabled={switchToEftLoadingId === registration?.id}
                              className="border border-teal-400 text-teal-700 hover:bg-teal-50 text-xs px-3 py-1.5 rounded-lg font-medium whitespace-nowrap flex items-center gap-1.5 transition-all disabled:opacity-60"
                            >
                              <Icon name={switchToEftLoadingId === registration?.id ? 'Loader' : 'RefreshCw'} size={12} className={switchToEftLoadingId === registration?.id ? 'animate-spin' : ''} />
                              {switchToEftLoadingId === registration?.id ? 'Değiştiriliyor...' : '🔄 EFT/Havale\'ye Çevir'}
                            </button>
                          </>
                        ) : isHavale(registration) ? (
                          <button
                            type="button"
                            onClick={() => handleHavaleApprove(registration)}
                            disabled={havaleApprovalLoadingId === registration?.id}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs px-3.5 py-2 rounded-lg shadow-sm transition-all flex items-center gap-1.5 disabled:opacity-60 whitespace-nowrap"
                          >
                            <Icon name={havaleApprovalLoadingId === registration?.id ? 'Loader' : 'Banknote'} size={13} className={havaleApprovalLoadingId === registration?.id ? 'animate-spin' : ''} />
                            {havaleApprovalLoadingId === registration?.id ? 'İşleniyor...' : '🏦 Onayla & IBAN Gönder'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                setProcessingId(registration?.id);
                                const { error: dbErr } = await supabase?.from('event_registrations')?.update({ status: 'approved' })?.eq('id', registration?.id);
                                if (dbErr) throw dbErr;
                                await loadRegistrations();
                                await loadStats();
                              } catch (err) {
                                setError(err?.message);
                              } finally {
                                setProcessingId(null);
                              }
                            }}
                            disabled={processingId === registration?.id}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs px-3.5 py-2 rounded-lg shadow-sm transition-all flex items-center gap-1.5 disabled:opacity-60 whitespace-nowrap"
                          >
                            <Icon name={processingId === registration?.id ? 'Loader' : 'CheckCircle'} size={13} className={processingId === registration?.id ? 'animate-spin' : ''} />
                            {processingId === registration?.id ? 'İşleniyor...' : 'Onayla'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRejectClick(registration?.id)}
                          disabled={processingId === registration?.id}
                          className="text-red-600 hover:bg-red-50 text-xs px-2.5 py-1.5 rounded-lg font-medium flex items-center gap-1.5 whitespace-nowrap transition-all"
                        >
                          <Icon name="XCircle" size={13} />
                          ❌ Reddet
                        </button>
                        {!isCreditCard(registration) && Number(registration?.totalPrice ?? calculateRegistrationTotal(registration)) > 0 && (
                          <button
                            type="button"
                            onClick={() => handleGeneratePaymentLink(registration)}
                            disabled={paytrLoadingId === registration?.id || isSendingLink}
                            className="border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs px-3 py-1.5 rounded-lg font-medium whitespace-nowrap flex items-center gap-1.5 transition-all disabled:opacity-60"
                          >
                            <Icon name={paytrLoadingId === registration?.id ? 'Loader' : 'Link'} size={12} className={paytrLoadingId === registration?.id ? 'animate-spin' : ''} />
                            {paytrLoadingId === registration?.id ? 'Oluşturuluyor...' : (registration?.paytrLink || registration?.paymentLink) ? 'Linki Yenile' : 'Ödeme Linki'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleCreateInvoiceDraft(registration)}
                          disabled={invoiceLoadingId === registration?.id}
                          className="border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs px-3 py-1.5 rounded-lg font-medium whitespace-nowrap flex items-center gap-1.5 transition-all disabled:opacity-60"
                        >
                          <Icon name={invoiceLoadingId === registration?.id ? 'Loader' : 'FileText'} size={12} className={invoiceLoadingId === registration?.id ? 'animate-spin' : ''} />
                          {invoiceLoadingId === registration?.id ? 'İşleniyor...' : 'Fatura Oluştur'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const current = registration?.totalPrice != null ? Number(registration?.totalPrice) : calculateRegistrationTotal(registration);
                            setDiscountInput(String(current));
                            setDiscountEditId(registration?.id);
                          }}
                          className="border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs px-3 py-1.5 rounded-lg font-medium whitespace-nowrap flex items-center gap-1.5 transition-all"
                        >
                          <Icon name="Pencil" size={12} />
                          Fiyat Düzenle
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(registration?.id)}
                          disabled={deletingId === registration?.id}
                          className="text-red-600 hover:bg-red-50 text-xs px-2.5 py-1.5 rounded-lg font-medium flex items-center gap-1.5 whitespace-nowrap transition-all disabled:opacity-60"
                        >
                          <Icon name="Trash2" size={12} />
                          Sil
                        </button>
                      </div>
                    )}

                    {/* ─── ACTION BUTTONS: APPROVED (waiting payment) status ─── */}
                    {isAwaitingPayment(registration?.status) && (
                      <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100 w-full md:w-auto md:flex-col md:min-w-[220px]">
                        <div className="w-full flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-xs font-semibold border border-blue-200">
                          💳 Ödeme Bekleniyor
                        </div>
                        {isFreeRegistration(registration) && (
                          <button
                            type="button"
                            onClick={() => handleFreeApprove(registration)}
                            disabled={processingId === registration?.id}
                            className="bg-green-600 hover:bg-green-700 text-white font-semibold text-xs px-3.5 py-2 rounded-lg shadow-sm transition-all flex items-center gap-1.5 disabled:opacity-60 whitespace-nowrap"
                          >
                            <Icon name={processingId === registration?.id ? 'Loader' : 'Gift'} size={13} className={processingId === registration?.id ? 'animate-spin' : ''} />
                            {processingId === registration?.id ? 'İşleniyor...' : '🎁 Ücretsiz Onayla & Ödendi İşaretle'}
                          </button>
                        )}
                        {isCreditCard(registration) && Number(registration?.totalPrice ?? calculateRegistrationTotal(registration)) > 0 && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleCreditCardApprove(registration)}
                              disabled={ccApprovalLoadingId === registration?.id}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs px-3.5 py-2 rounded-lg shadow-sm transition-all flex items-center gap-1.5 disabled:opacity-60 whitespace-nowrap"
                            >
                              <Icon name={ccApprovalLoadingId === registration?.id ? 'Loader' : 'RefreshCw'} size={13} className={ccApprovalLoadingId === registration?.id ? 'animate-spin' : ''} />
                              {ccApprovalLoadingId === registration?.id ? 'İşleniyor...' : 'Link Yenile & Gönder'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSwitchToEFT(registration?.id)}
                              disabled={switchToEftLoadingId === registration?.id}
                              className="border border-teal-400 text-teal-700 hover:bg-teal-50 text-xs px-3 py-1.5 rounded-lg font-medium whitespace-nowrap flex items-center gap-1.5 transition-all disabled:opacity-60"
                            >
                              <Icon name={switchToEftLoadingId === registration?.id ? 'Loader' : 'RefreshCw'} size={12} className={switchToEftLoadingId === registration?.id ? 'animate-spin' : ''} />
                              {switchToEftLoadingId === registration?.id ? 'Değiştiriliyor...' : '🔄 EFT/Havale\'ye Çevir'}
                            </button>
                          </>
                        )}
                        {!isCreditCard(registration) && Number(registration?.totalPrice ?? calculateRegistrationTotal(registration)) > 0 && (
                          <button
                            type="button"
                            onClick={() => handleGeneratePaymentLink(registration)}
                            disabled={paytrLoadingId === registration?.id || isSendingLink}
                            className="border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs px-3 py-1.5 rounded-lg font-medium whitespace-nowrap flex items-center gap-1.5 transition-all disabled:opacity-60"
                          >
                            <Icon name={paytrLoadingId === registration?.id ? 'Loader' : 'Link'} size={12} className={paytrLoadingId === registration?.id ? 'animate-spin' : ''} />
                            {paytrLoadingId === registration?.id ? 'Oluşturuluyor...' : 'Ödeme Linki'}
                          </button>
                        )}
                        {registration?.id && (
                          <button
                            type="button"
                            onClick={() => {
                              const safeGatewayUrl = `${window.location?.origin}/payment-gateway?regId=${registration?.id}`;
                              navigator.clipboard?.writeText(safeGatewayUrl)?.then(() => {
                                setPaytrToast({ type: 'success', message: 'Ödeme linki kopyalandı! WhatsApp veya SMS ile iletebilirsiniz.' });
                                setTimeout(() => setPaytrToast(null), 4000);
                              })?.catch((err) => {
                                console.error('Copy error:', err);
                                setPaytrToast({ type: 'error', message: 'Link kopyalanamadı.' });
                                setTimeout(() => setPaytrToast(null), 3000);
                              });
                            }}
                            className="border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs px-3 py-1.5 rounded-lg font-medium whitespace-nowrap flex items-center gap-1.5 transition-all"
                          >
                            <Icon name="Copy" size={12} />
                            Ödeme Linki Kopyala
                          </button>
                        )}
                        {!isCreditCard(registration) && (registration?.paytrLink || registration?.paytr_link || registration?.paymentLink || registration?.payment_link) && (
                          <button
                            type="button"
                            disabled={emailStatus?.[registration?.id] === 'sending'}
                            onClick={() => handleSendEmail(registration, registration?.paytrLink || registration?.paytr_link || registration?.paymentLink || registration?.payment_link)}
                            className="border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs px-3 py-1.5 rounded-lg font-medium whitespace-nowrap flex items-center gap-1.5 transition-all disabled:opacity-60"
                          >
                            <Icon name={emailStatus?.[registration?.id] === 'sending' ? 'Loader' : 'Mail'} size={12} className={emailStatus?.[registration?.id] === 'sending' ? 'animate-spin' : ''} />
                            {emailStatus?.[registration?.id] === 'sending' ? 'Gönderiliyor...' : emailStatus?.[registration?.id] === 'sent' ? '✅ Tekrar Gönder' : '📧 E-posta Gönder'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleCreateInvoiceDraft(registration)}
                          disabled={invoiceLoadingId === registration?.id}
                          className="border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs px-3 py-1.5 rounded-lg font-medium whitespace-nowrap flex items-center gap-1.5 transition-all disabled:opacity-60"
                        >
                          <Icon name={invoiceLoadingId === registration?.id ? 'Loader' : 'FileText'} size={12} className={invoiceLoadingId === registration?.id ? 'animate-spin' : ''} />
                          {invoiceLoadingId === registration?.id ? 'İşleniyor...' : 'Fatura Oluştur'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const current = registration?.totalPrice != null ? Number(registration?.totalPrice) : calculateRegistrationTotal(registration);
                            setDiscountInput(String(current));
                            setDiscountEditId(registration?.id);
                          }}
                          className="border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs px-3 py-1.5 rounded-lg font-medium whitespace-nowrap flex items-center gap-1.5 transition-all"
                        >
                          <Icon name="Pencil" size={12} />
                          Fiyat Düzenle
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCancelClick(registration?.id)}
                          disabled={processingId === registration?.id}
                          className="text-red-600 hover:bg-red-50 text-xs px-2.5 py-1.5 rounded-lg font-medium flex items-center gap-1.5 whitespace-nowrap transition-all disabled:opacity-60"
                        >
                          <Icon name="Ban" size={12} />
                          ❌ İptal Et
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(registration?.id)}
                          disabled={deletingId === registration?.id}
                          className="text-red-600 hover:bg-red-50 text-xs px-2.5 py-1.5 rounded-lg font-medium flex items-center gap-1.5 whitespace-nowrap transition-all disabled:opacity-60"
                        >
                          <Icon name="Trash2" size={12} />
                          Sil
                        </button>
                      </div>
                    )}

                    {/* ─── ACTION BUTTONS: PAID status ─── */}
                    {isPaidStatus(registration?.status) && (
                      <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100 w-full md:w-auto md:flex-col md:min-w-[220px]">
                        <div className="w-full flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200">
                          ✅ Ödeme Tamamlandı
                        </div>
                        <button
                          type="button"
                          onClick={() => handleCreateInvoiceDraft(registration)}
                          disabled={invoiceLoadingId === registration?.id}
                          className="border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs px-3 py-1.5 rounded-lg font-medium whitespace-nowrap flex items-center gap-1.5 transition-all disabled:opacity-60"
                        >
                          <Icon name={invoiceLoadingId === registration?.id ? 'Loader' : 'FileText'} size={12} className={invoiceLoadingId === registration?.id ? 'animate-spin' : ''} />
                          {invoiceLoadingId === registration?.id ? 'İşleniyor...' : 'Fatura Oluştur'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCancelClick(registration?.id)}
                          disabled={processingId === registration?.id}
                          className="text-red-600 hover:bg-red-50 text-xs px-2.5 py-1.5 rounded-lg font-medium flex items-center gap-1.5 whitespace-nowrap transition-all disabled:opacity-60"
                        >
                          <Icon name="Ban" size={12} />
                          ❌ İptal Et
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(registration?.id)}
                          disabled={deletingId === registration?.id}
                          className="text-red-600 hover:bg-red-50 text-xs px-2.5 py-1.5 rounded-lg font-medium flex items-center gap-1.5 whitespace-nowrap transition-all disabled:opacity-60"
                        >
                          <Icon name="Trash2" size={12} />
                          Sil
                        </button>
                      </div>
                    )}

                    {/* ─── ACTION BUTTONS: CANCELED / rejected / cancelled status ─── */}
                    {isCanceledStatus(registration?.status) && (
                      <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100 w-full md:w-auto md:flex-col md:min-w-[220px]">
                        <button
                          type="button"
                          onClick={() => {
                            const current = registration?.totalPrice != null ? Number(registration?.totalPrice) : calculateRegistrationTotal(registration);
                            setDiscountInput(String(current));
                            setDiscountEditId(registration?.id);
                          }}
                          className="border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs px-3 py-1.5 rounded-lg font-medium whitespace-nowrap flex items-center gap-1.5 transition-all"
                        >
                          <Icon name="Pencil" size={12} />
                          Fiyat Düzenle
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(registration?.id)}
                          disabled={deletingId === registration?.id}
                          className="text-red-600 hover:bg-red-50 text-xs px-2.5 py-1.5 rounded-lg font-medium flex items-center gap-1.5 whitespace-nowrap transition-all disabled:opacity-60"
                        >
                          <Icon name="Trash2" size={12} />
                          Sil
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center">
              <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">
                {searchTerm || statusFilter !== 'all' ? 'Filtrelere uygun rezervasyon bulunamadı' : 'Bu etkinlik için henüz rezervasyon bulunmuyor'}
              </p>
            </div>
          )}
        </div>

        </div>{/* end scrollable middle section */}

        {/* Footer */}
        <div className="flex-none p-4 border-t border-gray-100 bg-gray-50/50">
          <Button variant="outline" onClick={onClose} className="w-full">
            Kapat
          </Button>
        </div>
      </div>
      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-card rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center">
                <Icon name="XCircle" size={24} className="text-error" />
              </div>
              <h3 className="text-xl font-semibold text-foreground">Rezervasyonu Reddet</h3>
            </div>
            <p className="text-muted-foreground mb-4">Lütfen reddetme nedeninizi belirtin:</p>
            <textarea
              value={rejectionData?.reason}
              onChange={(e) => setRejectionData({ ...rejectionData, reason: e?.target?.value })}
              placeholder="Reddetme nedeni..."
              className="w-full min-h-[100px] p-3 rounded-lg border border-border bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <div className="flex gap-3 mt-4">
              <Button
                variant="outline"
                onClick={() => { setShowRejectModal(false); setRejectionData({ id: null, reason: '' }); }}
                className="flex-1"
              >
                İptal
              </Button>
              <Button
                variant="danger"
                onClick={handleRejectConfirm}
                disabled={!rejectionData?.reason?.trim()}
                className="flex-1"
              >
                Reddet
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Cancellation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-card rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center">
                <Icon name="Ban" size={24} className="text-error" />
              </div>
              <h3 className="text-xl font-semibold text-foreground">Onaylı Rezervasyonu İptal Et</h3>
            </div>
            <p className="text-muted-foreground mb-4">
              Bu onaylı rezervasyonu iptal etmek istediğinizden emin misiniz? İptal nedeni (opsiyonel):
            </p>
            <textarea
              value={cancellationData?.reason}
              onChange={(e) => setCancellationData({ ...cancellationData, reason: e?.target?.value })}
              placeholder="İptal nedeni (opsiyonel)..."
              className="w-full min-h-[100px] p-3 rounded-lg border border-border bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <div className="flex gap-3 mt-4">
              <Button
                variant="outline"
                onClick={() => { setShowCancelModal(false); setCancellationData({ id: null, reason: '' }); }}
                className="flex-1"
              >
                Vazgeç
              </Button>
              <Button variant="danger" onClick={handleCancelConfirm} className="flex-1">
                İptal Et
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-card rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center">
                <Icon name="Trash2" size={24} className="text-error" />
              </div>
              <h3 className="text-xl font-semibold text-foreground">Rezervasyonu Sil</h3>
            </div>
            <p className="text-muted-foreground mb-2">
              Bu rezervasyonu kalıcı olarak silmek istediğinizden emin misiniz?
            </p>
            <p className="text-sm text-error font-medium mb-4">Bu işlem geri alınamaz.</p>
            <div className="flex gap-3 mt-4">
              <Button
                variant="outline"
                onClick={() => { setShowDeleteModal(false); setDeleteTargetId(null); }}
                className="flex-1"
                disabled={deletingId === deleteTargetId}
              >
                Vazgeç
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteConfirm}
                disabled={deletingId === deleteTargetId}
                className="flex-1"
              >
                {deletingId === deleteTargetId ? 'Siliniyor...' : 'Evet, Sil'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventRegistrationsModal;